import { useRef, useState } from 'react';
import { Link, useParams } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import type { ReadingPlanDetail } from '../../shared/api/types';
import { Alert, Button, Card, EmptyState, ErrorState, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { bibleLink } from '../bible/BiblePage';
import { useMe } from '../session/SessionProvider';
import { progressLabel } from './DevotionalPage';
import { StartTogether } from './ReadTogether';
import { DayConversation } from './NotebookPage';

type Day = ReadingPlanDetail['days'][number];

function Passages({ passages }: { passages: string[] }) {
  return (
    <span className="plan__passages">
      {passages.map((p) => (
        <Link key={p} to={bibleLink(p)}>
          {p}
        </Link>
      ))}
    </span>
  );
}

/** Un plan: la lectura de hoy, todos los días y mi avance (privado). */
export function PlanPage() {
  const { planId = '' } = useParams();
  const me = useMe();
  const churchId = me.activeChurchId;
  const [version, setVersion] = useState(0);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const plan = useAsync(
    (signal) => (churchId ? api.get<ReadingPlanDetail>(`/devotional/churches/${churchId}/plans/${planId}`, signal) : Promise.reject(new Error('Sin contexto'))),
    [churchId, planId, version],
  );
  const last = useRef<ReadingPlanDetail | null>(null);
  if (plan.status === 'ready') last.current = plan.data;
  const p = plan.status === 'ready' ? plan.data : last.current;

  async function run(key: string, method: 'post' | 'put', path: string, body?: unknown) {
    setBusy(key);
    setError(null);
    try {
      await (method === 'post' ? api.post(`/devotional/churches/${churchId}/plans/${planId}${path}`, body) : api.put(`/devotional/churches/${churchId}/plans/${planId}${path}`, body));
      setVersion((v) => v + 1);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(null);
    }
  }

  if (!churchId) return <EmptyState title="Elige primero la iglesia en la que actúas." />;
  const active = p?.mine?.status === 'active' ? p.mine : null;
  const today = active ? p!.days.find((d) => d.day === active.today) : undefined;
  const canManage = me.can('devotional.manage');

  return (
    <div className="page page--narrow">
      <p className="small">
        <Link to="/app/devocional">← Planes de lectura</Link>
      </p>
      {plan.status === 'loading' && !p ? <Loading /> : null}
      {plan.status === 'error' ? <ErrorState error={plan.error} onRetry={plan.reload} /> : null}
      {p ? (
        <>
          <header className="page__header">
            <h1>{p.title}</h1>
            <p className="muted">
              {p.totalDays} {p.totalDays === 1 ? 'día' : 'días'}
              {progressLabel(p) ? ` · ${progressLabel(p)}` : ''}
            </p>
            {p.description ? <p>{p.description}</p> : null}
          </header>
          {error ? <Alert>{error}</Alert> : null}
          {p.mine?.status === 'finished' && !active ? <Alert tone="success">Terminaste este plan.</Alert> : null}

          <div className="actions actions--wrap">
            {p.status === 'published' && !active ? (
              <Button busy={busy === 'enroll'} onClick={() => run('enroll', 'post', '/enroll')}>
                {p.mine ? 'Empezar de nuevo' : 'Empezar hoy'}
              </Button>
            ) : null}
            {canManage && p.status !== 'archived' ? (
              <Link className="btn btn--secondary" to={`/app/devocional/${p.id}/editar`}>
                <span>Editar</span>
              </Link>
            ) : null}
            {canManage && p.status === 'draft' ? (
              <Button busy={busy === 'publish'} onClick={() => run('publish', 'post', '/publish')}>
                Publicar
              </Button>
            ) : null}
            {canManage && p.status !== 'archived' ? (
              <Button variant="ghost" busy={busy === 'archive'} onClick={() => { if (window.confirm('¿Archivar este plan? Ya no aparecerá para leer en la iglesia.')) void run('archive', 'post', '/archive'); }}>
                Archivar
              </Button>
            ) : null}
          </div>
          {p.status === 'published' ? <StartTogether planId={p.id} /> : null}
          {canManage && p.enrolled !== null && p.status !== 'draft' ? <p className="muted small">{p.enrolled} {p.enrolled === 1 ? 'persona lo sigue' : 'personas lo siguen'}. El avance de cada quien es privado.</p> : null}

          {active && today ? (
            <Card title={`Hoy · día ${today.day}${today.title ? `: ${today.title}` : ''}`}>
              <Passages passages={today.passages} />
              {today.reflection ? <p className="reading">{today.reflection}</p> : null}
              {p.status === 'published' ? <DayConversation planId={p.id} day={today.day} /> : null}
              <DayCheck day={today} checked={active.completed.includes(today.day)} busy={busy === `day-${today.day}`} onToggle={(done) => run(`day-${today.day}`, 'put', `/days/${today.day}`, { done })} />
            </Card>
          ) : null}

          <Card title="Todos los días">
            <ul className="list">
              {p.days.map((d) => (
                <li key={d.day} className="list__item plan__day">
                  {active ? (
                    <DayCheck day={d} checked={active.completed.includes(d.day)} busy={busy === `day-${d.day}`} compact onToggle={(done) => run(`day-${d.day}`, 'put', `/days/${d.day}`, { done })} />
                  ) : null}
                  <span>
                    <strong>
                      Día {d.day}
                      {d.title ? `: ${d.title}` : ''}
                    </strong>{' '}
                    <Passages passages={d.passages} />
                  </span>
                </li>
              ))}
            </ul>
          </Card>
          {active ? (
            <div className="actions">
              <Button variant="ghost" busy={busy === 'leave'} onClick={() => { if (window.confirm('¿Dejar este plan? Dejarás de seguirlo y ya no aparecerá en tu Hoy.')) void run('leave', 'post', '/leave'); }}>
                Dejar este plan
              </Button>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

function DayCheck({ day, checked, busy, compact = false, onToggle }: { day: Day; checked: boolean; busy: boolean; compact?: boolean; onToggle: (done: boolean) => void }) {
  return (
    <label className="choice">
      <input type="checkbox" checked={checked} disabled={busy} onChange={(e) => onToggle(e.target.checked)} aria-label={compact ? `Día ${day.day} leído` : undefined} />
      {compact ? null : <span>Ya lo leí</span>}
    </label>
  );
}
